// Meditation Frome - Service Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, User, Users } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../../components/shared/SEOHelper";

const MeditationFrome = () => {
  usePageMeta({
    title: "Meditation Classes Frome | Guided Meditation Somerset",
    description: "Meditation classes and private meditation sessions in Frome, Somerset. A simple yet powerful way to quiet the mind, reduce anxiety and reconnect with",
    canonical: "https://annelielily.com/yoga-classes/meditation"
  });

  const serviceSchema = generateServiceSchema({
    name: "Meditation in Frome",
    description: "Meditation classes and private meditation sessions in Frome, Somerset with Annelie Lily.",
    serviceType: "Meditation Class",
    url: "https://annelielily.com/yoga-classes/meditation"
  });

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Meditation in Frome
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Meditation
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Alongside her yoga teaching, Annelie also offers meditation classes and private meditation sessions. Meditation provides a simple yet powerful way to quiet the mind, reduce anxiety and reconnect with a deeper sense of inner calm.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">
              Enquire About Meditation <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Finding Stillness
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Whether you are new to meditation or wish to deepen your practice, Annelie gently guides you into calm and stillness.
              </p>
              <p>
                Sessions may include guided meditation, breath awareness, visualisation, and techniques to help you develop a regular practice you can continue at home.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Options */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Ways to Practice Meditation
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <Users className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-heading text-xl text-neutral-800 mb-2">Meditation Classes</h3>
                <p className="text-neutral-600 text-sm mb-4">
                  Group sessions offering guided meditation in a supportive environment. Often combined with gentle movement or breathwork.
                </p>
                <Link to="/contact" className="text-gold text-sm font-medium hover:underline inline-flex items-center gap-1">
                  Enquire about classes <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="bg-white/70 p-6 rounded-xl border border-gold/10">
                <User className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-heading text-xl text-neutral-800 mb-2">Private Sessions</h3>
                <p className="text-neutral-600 text-sm mb-4">
                  One-to-one meditation instruction tailored to your needs and experience level. Learn techniques you can practice at home.
                </p>
                <Link to="/contact" className="text-gold text-sm font-medium hover:underline inline-flex items-center gap-1">
                  Enquire about private sessions <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Benefits of Meditation
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
              <ul className="space-y-3">
                {[
                  "Quiet the mind and reduce mental chatter",
                  "Reduce anxiety and stress",
                  "Reconnect with a deeper sense of inner calm",
                  "Improve focus and clarity",
                  "Support emotional wellbeing",
                  "Develop tools for daily life"
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-700">
                    <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Begin?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Whether you're completely new to meditation or looking to deepen an existing practice, get in touch to find out more.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="bottom-cta">
              Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/yoga-classes/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/womens-circles" className="text-sm text-gold hover:underline">Women's Circles</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/retreats" className="text-sm text-gold hover:underline">Retreats</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MeditationFrome;
